import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import type { SubscriptionView } from '../../api/generated/models/SubscriptionView';
import { helper } from '../../utils/helper';

export interface WithLicenseCheckProps {
  pluginName: string;
}

/**
 * Render-only license guard. The subscription list is owned by the centralized
 * bootstrap (`bootstrapSession`) and refreshed by user-driven actions
 * (purchase / cancel). HOCs MUST NOT trigger their own fetches - that was the
 * source of duplicate `/subscription/list` requests on every page mount.
 */
export const withLicenseCheck = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  pluginName: string
) => {
  return (props: P) => {
    const subscriptions = useSelector((state: RootState) => state.licenses.items);

    const hasValidSubscription = subscriptions.some(
      (subscription: SubscriptionView) => subscription.pluginName === pluginName && helper.isSubscriptionActive(subscription)
    );

    if (!hasValidSubscription) {
      return (
        <div>
          <h2>Subscription Required</h2>
          <p>You need a valid subscription to access this feature.</p>
          <p>Please purchase a subscription for {pluginName}.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export const withLicenseCheckHOC = <P extends object>(
  pluginName: string
) => {
  return (WrappedComponent: React.ComponentType<P>) => {
    const WithLicenseCheckComponent = (props: P) => {
      const { items: subscriptions, loading, error } = useSelector((state: RootState) => state.licenses);

      if (loading || error) {
        return null;
      }

      const hasValidSubscription = subscriptions.some(
        (subscription: SubscriptionView) => subscription.pluginName === pluginName && helper.isSubscriptionActive(subscription)
      );

      if (!hasValidSubscription) {
        return null;
      }

      return <WrappedComponent {...props} />;
    };

    WithLicenseCheckComponent.displayName = `WithLicenseCheck(${
      WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`;

    return WithLicenseCheckComponent;
  };
};
